import { create } from 'zustand';
import type { CanvasElement } from '../types/index';
import { expandSubtree, cloneSubtreeWithNewIds } from '../lib/groupUtils';

const PASTE_OFFSET = 20;

interface ClipboardState {
  copiedElements: string | null;
  pasteCount: number;

  /**
   * Copy the given selection. `allElements` is the full canvas element list
   * used to pull in descendants of any GroupElement in the selection so the
   * clipboard payload is self-contained.
   */
  copy: (selected: CanvasElement[], allElements: CanvasElement[]) => void;
  paste: () => CanvasElement[] | null;
  hasContent: () => boolean;
}

export const useClipboardStore = create<ClipboardState>((set, get) => ({
  copiedElements: null,
  pasteCount: 0,

  copy: (selected, allElements) => {
    if (selected.length === 0) return;
    const rootIds = selected.map((e) => e.id);
    const subtree = expandSubtree(allElements, rootIds);
    set({ copiedElements: JSON.stringify(subtree), pasteCount: 0 });
  },

  paste: () => {
    const { copiedElements, pasteCount } = get();
    if (!copiedElements) return null;

    try {
      const parsed = JSON.parse(copiedElements) as CanvasElement[];
      const { elements: cloned } = cloneSubtreeWithNewIds(parsed);
      const offset = (pasteCount + 1) * PASTE_OFFSET;

      const originalIdSet = new Set(parsed.map((e) => e.id));
      const childIdSet = new Set<string>();
      for (const el of parsed) {
        if (el.type === 'group') {
          for (const cid of el.childIds) {
            if (originalIdSet.has(cid)) childIdSet.add(cid);
          }
        }
      }

      // Only offset the "root" elements of the pasted subtree; offsetting
      // every descendant would double-shift any child of a pasted group.
      const shifted = cloned.map((el, i) => {
        const original = parsed[i];
        const isChild = childIdSet.has(original.id);
        if (isChild) return el;
        return {
          ...el,
          x: (el.x ?? 0) + offset,
          y: (el.y ?? 0) + offset,
          name: el.name ? `${el.name} copy` : el.name,
        };
      });

      set({ pasteCount: pasteCount + 1 });
      return shifted;
    } catch {
      return null;
    }
  },

  hasContent: () => get().copiedElements !== null,
}));
